/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/*
 * The file is automatically generated by Serenity UI.
 *
 * ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
 *
 * Found an issue? Submit a task to the ClickUp board:
 * https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
 */
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type HTMLAttributes, createContext, forwardRef } from 'react';
import { type ContextValue, DEFAULT_SLOT, Provider, ToolbarContext, useContextProps } from 'react-aria-components';
import { AddPlusStrokeIcon } from '../../icons/add-plus-stroke/add-plus-stroke.icon';
import { MoreVerticalStrokeIcon } from '../../icons/more-vertical-stroke/more-vertical-stroke.icon';
import { HeadingContext } from '../heading/heading.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { Icon } from '../icon/icon.component';
import { SearchFieldContext } from '../search-field/search-field.component';
import { TEXT_BUTTON_SLOTS, TextButtonContext } from '../text-button/text-button.component';
import { Text, TextContext } from '../text/text.component';
import { ViewContext } from '../view/view.component';
import { $ } from '../../tokens.stylex';

type TableViewVariant = 'fullscreen' | 'inline' | 'embedded';

interface TableViewProps extends Omit<HTMLAttributes<HTMLElement>, 'style' | 'className'> {
  /** The layout variant of the table view. */
  variant?: TableViewVariant;
  /** Whether the table view is highlighted. */
  isHighlighted?: boolean;
  style?: stylex.StyleXStyles;
}

const TableViewContext = createContext<ContextValue<TableViewProps, HTMLDivElement>>({});

const TableView = forwardRef((props: TableViewProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, TableViewContext);

  const { style, children, variant = 'embedded', isHighlighted = false, ...coreProps } = props;

  const isCompact = variant !== 'fullscreen';

  return (
    <section ref={ref} {...coreProps} {...stylex.props(styles.base, variant !== 'fullscreen' && styles[variant], isHighlighted && styles.highlighted, style)}>
      <Provider
        values={[
          [
            ViewContext,
            {
              slots: {
                [DEFAULT_SLOT]: {},
                [TABLE_VIEW_SLOTS.HEADER_VIEW]: {
                  style: [headerViewStyles.base, headerViewVariantVariants[isCompact ? 'embedded' : 'fullScreen']],
                },
              },
            },
          ],
          [
            HeadingContext,
            {
              level: isCompact ? 2 : 1,
              size: isCompact ? 18 : 24,
            },
          ],
          [
            SearchFieldContext,
            {
              size: 36,
              isRounded: true,
              style: searchFieldStyles.base,
            },
          ],
          [
            ToolbarContext,
            stylex.props(
              toolbarStyles.base,
              isCompact ? toolbarVariantVariants.embedded : toolbarVariantVariants.fullScreen,
            ),
          ],
          [
            IconButtonContext,
            {
              slots: {
                [DEFAULT_SLOT]: {
                  size: 32,
                  variant: 'ghost',
                  isRounded: true,
                },
                [TABLE_VIEW_SLOTS.ADD_ICON_BUTTON]: {
                  size: 32,
                  variant: 'ghost',
                  isRounded: true,
                  'aria-label': 'Add',
                  children: <AddPlusStrokeIcon />,
                },
                [TABLE_VIEW_SLOTS.MORE_ICON_BUTTON]: {
                  size: 32,
                  variant: 'ghost',
                  isRounded: true,
                  'aria-label': 'More',
                  children: <MoreVerticalStrokeIcon />,
                },
              },
            },
          ],
          [
            TextContext,
            {
              slots: {
                [DEFAULT_SLOT]: {},
                [TABLE_VIEW_SLOTS.EMPTY_STATE_TEXT]: {
                  size: 13,
                  color: 'placeholder',
                  children: 'No data yet',
                  style: footerActionStyles.base,
                },
              },
            },
          ],
          [
            TextButtonContext,
            {
              slots: {
                [DEFAULT_SLOT]: {},
                [TABLE_VIEW_SLOTS.MORE_TEXT_BUTTON]: {
                  size: 32,
                  variant: 'tertiary',
                  style: [footerActionStyles.base, textButtonStyles.base],
                  children: (renderProps) => (
                    <>
                      <Text {...(renderProps.isHovered && { color: 'interactive' })}>More</Text>
                      <Icon
                        {...(renderProps.isHovered && { color: 'interactive' })}
                        slot={TEXT_BUTTON_SLOTS.RIGHT_ICON}
                        size={12}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none">
                          <path d="M2 4L6 8L10 4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </Icon>
                    </>
                  ),
                },
                [TABLE_VIEW_SLOTS.ADD_TEXT_BUTTON]: {
                  size: 32,
                  variant: 'tertiary',
                  style: [footerActionStyles.base, textButtonStyles.base],
                  children: (renderProps) => (
                    <>
                      <Icon
                        {...(renderProps.isHovered && { color: 'interactive' })}
                        slot={TEXT_BUTTON_SLOTS.LEFT_ICON}
                        size={16}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none">
                          <path d="M8.00039 2.4V13.6M13.6004 8L2.40039 8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </Icon>
                      <Text {...(renderProps.isHovered && { color: 'interactive' })}>Add</Text>
                    </>
                  ),
                },
              },
            },
          ],
        ]}
      >
        {children}
      </Provider>
    </section>
  );
});

const styles = stylex.create({
  base: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    maxWidth: '100%',
    height: '100%',
    width: '100%',
  },
  inline: {
    height: 'fit-content',
    width: '100%',
  },
  embedded: {
    height: 'fit-content',
    maxWidth: '100%',
    overflow: 'hidden',
    width: 'fit-content',
  },
  highlighted: {
    borderColor: $.accentChilli60,
    boxShadow: `0 0 8px 4px ${$.accentChilli10}`,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
  },
});

const headerViewStyles = stylex.create({
  base: {
    alignItems: 'center',
    alignSelf: 'stretch',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    display: 'flex',
  },
});

const headerViewVariantVariants = stylex.create({
  fullScreen: {
    gap: 16,
    paddingBlock: 12,
    paddingInline: 16,
    height: 56,
  },
  embedded: {
    gap: 4,
    height: 40,
    paddingRight: 4,
    paddingLeft: 8,
  },
});

const searchFieldStyles = stylex.create({
  base: {
    width: 264,
  },
});

const toolbarStyles = stylex.create({
  base: {
    display: 'flex',
    marginLeft: 'auto',
  },
});

const toolbarVariantVariants = stylex.create({
  fullScreen: { gap: 8 },
  embedded: { gap: 4 },
});

const footerActionStyles = stylex.create({
  base: {
    paddingBlock: 7,
    paddingInline: 8,
    width: '100%',
  },
});

const textButtonStyles = stylex.create({
  base: {
    justifyContent: 'flex-start',
  },
});

const TABLE_VIEW_SLOTS = {
  HEADER_VIEW: 'header-view',
  MORE_ICON_BUTTON: 'more-icon-button',
  ADD_ICON_BUTTON: 'add-icon-button',
  EMPTY_STATE_TEXT: 'empty-state-text',
  MORE_TEXT_BUTTON: 'more-text-button',
  ADD_TEXT_BUTTON: 'add-text-button',
};

export { TABLE_VIEW_SLOTS, TableView, TableViewContext, type TableViewProps, type TableViewVariant };
